import { useState, useCallback } from "react";

/**
 * useAnalysisState — consolidated state for the entire analyze→verdict→unlock
 * flow. Replaces 7 separate useState calls scattered across <Home/> with a
 * single object that exposes semantic actions instead of raw setters.
 *
 * Usage:
 *   const analysisState = useAnalysisState();
 *   analysisState.analysis          // current analysis result (or null)
 *   analysisState.showVerdict       // is the pre-paywall verdict screen shown
 *   analysisState.pdfUnlocked       // has the user paid £1 to unlock
 *   analysisState.paymentLoading    // Stripe checkout loading (initial £1)
 *   analysisState.paymentError      // Stripe error (initial £1)
 *   analysisState.pdfPaymentLoading // PDF download payment loading
 *   analysisState.pdfPaymentError   // PDF download payment error
 *
 *   analysisState.openVerdict(result)   // set analysis + open verdict screen
 *   analysisState.closeVerdict()         // hide verdict screen (used for unlocked → go to dashboard)
 *   analysisState.reset()                // clear everything (used by "new search")
 *   analysisState.setUnlocked(true)      // mark PDF unlocked after Stripe success
 *   analysisState.setAnalysis(next)      // replace the analysis (preliminary → final swap)
 *   analysisState.startPayment()         // begin Stripe checkout (loading on, error clear)
 *   analysisState.failPayment(err)       // end Stripe checkout with error
 *   analysisState.resetPayment()         // just clear paymentLoading/Error (used on back)
 *   analysisState.startPdfPayment()      // begin PDF unlock payment
 *   analysisState.failPdfPayment(err)    // end PDF unlock with error
 *   analysisState.resetPdfPayment()      // clear pdfPaymentLoading/Error
 */
export function useAnalysisState() {
  const [analysis, setAnalysis] = useState(null);
  const [showVerdict, setShowVerdict] = useState(false);
  const [pdfUnlocked, setPdfUnlocked] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [pdfPaymentLoading, setPdfPaymentLoading] = useState(false);
  const [pdfPaymentError, setPdfPaymentError] = useState(null);

  const openVerdict = useCallback((result) => {
    if (result) setAnalysis(result);
    setShowVerdict(true);
  }, []);

  const closeVerdict = useCallback(() => setShowVerdict(false), []);

  const reset = useCallback(() => {
    setAnalysis(null);
    setShowVerdict(false);
    setPaymentError(null);
    setPdfPaymentError(null);
    setPaymentLoading(false);
    setPdfPaymentLoading(false);
  }, []);

  const setUnlocked = useCallback((v) => setPdfUnlocked(!!v), []);

  const startPayment = useCallback(() => {
    setPaymentLoading(true);
    setPaymentError(null);
  }, []);

  const failPayment = useCallback((err) => {
    setPaymentError(err || 'Payment failed');
    setPaymentLoading(false);
  }, []);

  const resetPayment = useCallback(() => {
    setPaymentLoading(false);
    setPaymentError(null);
  }, []);

  const startPdfPayment = useCallback(() => {
    setPdfPaymentLoading(true);
    setPdfPaymentError(null);
  }, []);

  const failPdfPayment = useCallback((err) => {
    setPdfPaymentError(err || 'Payment failed');
    setPdfPaymentLoading(false);
  }, []);

  const resetPdfPayment = useCallback(() => {
    setPdfPaymentLoading(false);
    setPdfPaymentError(null);
  }, []);

  return {
    // State
    analysis,
    showVerdict,
    pdfUnlocked,
    paymentLoading,
    paymentError,
    pdfPaymentLoading,
    pdfPaymentError,
    // Raw setters (kept for edge-case integrations with existing async/Stripe handlers)
    setAnalysis,
    setShowVerdict,
    setPdfUnlocked,
    setPaymentLoading,
    setPaymentError,
    setPdfPaymentLoading,
    setPdfPaymentError,
    // Semantic actions
    openVerdict,
    closeVerdict,
    reset,
    setUnlocked,
    startPayment,
    failPayment,
    resetPayment,
    startPdfPayment,
    failPdfPayment,
    resetPdfPayment,
  };
}
